import React from 'react'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import { Button, KeyCard, Marquee, Reveal } from '@/design-system'
import { marqueeAlbums, photo } from './data'
import { APP_NAME } from '@/utils/constants'

const Header = styled('header')({
  position: 'relative',
  paddingTop: 'clamp(96px, 18vw, 150px)',
  overflow: 'hidden',
})

const Gridlines = styled(Box)({
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  display: 'grid',
  gridTemplateColumns: 'repeat(12, 1fr)',
  maxWidth: 1240,
  margin: '0 auto',
  padding: '0 clamp(20px, 4vw, 48px)',
  '@media (max-width: 960px)': { display: 'none' },
})

const GridCol = styled('i')({
  borderLeft: '1px solid rgba(13, 13, 15, 0.035)',
  '&:last-child': { borderRight: '1px solid rgba(13, 13, 15, 0.035)' },
})

const Wrap = styled(Box)({
  position: 'relative',
  maxWidth: 1240,
  margin: '0 auto',
  padding: '0 clamp(20px, 4vw, 48px)',
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 7fr) minmax(0, 5fr)',
  gap: 'clamp(24px, 4vw, 64px)',
  alignItems: 'end',
  '@media (max-width: 960px)': { gridTemplateColumns: '1fr' },
})

const Kicker = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 10,
  letterSpacing: '0.18em',
  color: 'var(--ds-ink-subtle)',
  textTransform: 'uppercase',
})

const DotSet = styled(Box)({ display: 'inline-flex', gap: 4 })
const Dot = styled('i')({ width: 7, height: 7, borderRadius: '50%', display: 'block' })

const H1 = styled('h1')({
  margin: 0,
  marginTop: 30,
  fontFamily: 'var(--ds-font)',
  fontSize: 'clamp(48px, 6.8vw, 92px)',
  fontWeight: 780,
  letterSpacing: '-0.052em',
  lineHeight: 0.94,
  color: 'var(--ds-ink)',
})

const Thin = styled('span')({ fontWeight: 380, letterSpacing: '-0.045em' })

const Addr = styled('span')({
  position: 'relative',
  whiteSpace: 'nowrap',
  '@media (max-width: 480px)': { whiteSpace: 'normal' },
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: '0.04em',
    height: '0.09em',
    background: 'var(--ds-gradient-spectrum)',
    borderRadius: 3,
    opacity: 0.9,
  },
})

const Lede = styled('p')({
  marginTop: 30,
  maxWidth: 460,
  fontSize: 16.5,
  lineHeight: 1.62,
  color: 'var(--ds-ink-muted)',
  letterSpacing: '-0.008em',
  '& b': { color: 'var(--ds-ink)', fontWeight: 640 },
})

const CtaRow = styled(Box)({
  marginTop: 36,
  display: 'flex',
  alignItems: 'center',
  gap: 18,
  flexWrap: 'wrap',
})

const Meta = styled(Box)({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9,
  letterSpacing: '0.14em',
  color: 'var(--ds-ink-subtle)',
  textTransform: 'uppercase',
  lineHeight: 1.9,
})

const Art = styled(Box)({
  position: 'relative',
  height: 520,
  '@media (max-width: 960px)': { height: 420 },
  '@media (max-width: 640px)': { height: 360, marginTop: 8 },
})

const Phone = styled(Box)({
  position: 'absolute',
  right: '8%',
  bottom: 0,
  width: 'min(270px, 62%)',
  borderRadius: '34px 34px 0 0',
  background: '#0a0a0c',
  padding: '9px 9px 0',
  boxShadow: 'var(--ds-shadow-deep)',
})

const PhoneScreen = styled(Box)({
  borderRadius: '26px 26px 0 0',
  overflow: 'hidden',
  background: 'var(--ds-night-canvas)',
  position: 'relative',
})

const PhoneImg = styled('img')({
  aspectRatio: '3 / 4.4',
  objectFit: 'cover',
  opacity: 0.92,
  width: '100%',
  display: 'block',
})

const PhoneBar = styled(Box)({
  position: 'absolute',
  left: 9,
  right: 9,
  bottom: 0,
  padding: '12px 14px',
  background: 'linear-gradient(0deg, rgba(10,10,12,0.92), transparent)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
})

const KeyCardWrap = styled(Box)({
  position: 'absolute',
  left: '4%',
  bottom: 34,
  transform: 'rotate(-4deg)',
  zIndex: 3,
  '@media (max-width: 640px)': {
    left: 0,
    bottom: 20,
    transform: 'rotate(-4deg) scale(0.82)',
    transformOrigin: 'left bottom',
  },
})

const ScanBeam = styled(Box)({
  position: 'absolute',
  left: '17%',
  bottom: 150,
  width: 150,
  height: 1.5,
  background: 'linear-gradient(90deg, transparent, var(--ds-accent-deep-b) 30%, var(--ds-accent-deep-b) 70%, transparent)',
  zIndex: 4,
  animation: 'ds-beam 3s var(--ds-ease) infinite',
  '@keyframes ds-beam': {
    '0%, 100%': { opacity: 0, transform: 'translateY(30px)' },
    '40%, 62%': { opacity: 1, transform: 'translateY(0)' },
  },
})

const Arrival = styled(Box)({
  position: 'absolute',
  right: 0,
  top: '8%',
  zIndex: 4,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '9px 14px',
  borderRadius: 'var(--ds-radius-pill)',
  background: '#fff',
  boxShadow: 'var(--ds-shadow-lift)',
  fontSize: 12,
  fontWeight: 620,
  animation: 'ds-floaty 5.5s var(--ds-ease) infinite',
  maxWidth: 'min(240px, 92vw)',
  '@media (max-width: 640px)': {
    fontSize: 11,
    padding: '7px 11px',
    maxWidth: 'calc(100% - 8px)',
  },
  '@keyframes ds-floaty': {
    '0%, 100%': { transform: 'translateY(0)' },
    '50%': { transform: 'translateY(-7px)' },
  },
})

const ArrivalAvatar = styled('span')({
  width: 20,
  height: 20,
  borderRadius: '50%',
  color: '#4a1e0a',
  fontSize: 8.5,
  fontWeight: 800,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const Base = styled(Box)({
  marginTop: 74,
  borderTop: '1px solid var(--ds-border)',
})

/** Hero + album marquee — the top of Landing v2. */
export function LandingHero() {
  const navigate = useNavigate()

  return (
    <Header>
      <Gridlines>
        {Array.from({ length: 12 }, (_, i) => (
          <GridCol key={i} />
        ))}
      </Gridlines>
      <Wrap>
        <Box>
          <Reveal>
            <Kicker>
              <DotSet>
                <Dot sx={{ background: '#ffcf7d' }} />
                <Dot sx={{ background: '#fea67d' }} />
                <Dot sx={{ background: '#be9dfd' }} />
                <Dot sx={{ background: '#01dafe' }} />
              </DotSet>
              The photo album, rebuilt as a place
            </Kicker>
          </Reveal>
          <Reveal delay={60}>
            <H1>
              Give your photos
              <br />
              <Thin>an</Thin> <Addr>address.</Addr>
            </H1>
          </Reveal>
          <Reveal delay={120}>
            <Lede>
              Feeds compress and forget. Drives bury and lock. {APP_NAME} gives every album{' '}
              <b>a door people walk through</b> — full-resolution rooms with real conversation,
              opened by a single key.
            </Lede>
          </Reveal>
          <Reveal delay={180}>
            <CtaRow>
              <Button variant='primary' size='lg' onClick={() => navigate('/auth?step=signup')}>
                Create your album →
              </Button>
              {/*
                Used to point at `/a/demo` — no seeded album exists under
                that id, so it silently landed on GuestAlbumScreen's "Album
                unavailable" empty state. Routed to signup instead until
                there's a real seeded public demo gallery/space to walk
                into; swap back to a real showcase album/gallery id once
                one exists instead of signup.
              */}
              <Button variant='secondary' size='lg' onClick={() => navigate('/auth?step=signup')}>
                Walk into one
              </Button>
              <Meta>
                FREE FOREVER PLAN
                <br />
                NO APP FOR GUESTS · EVER
              </Meta>
            </CtaRow>
          </Reveal>
        </Box>

        <Reveal delay={100}>
          <Art>
            <Arrival>
              <ArrivalAvatar sx={{ background: '#fea67d' }}>S</ArrivalAvatar>
              Sarah just walked in
            </Arrival>
            <Arrival sx={{ top: '30%', right: '4%', animationDelay: '1s', '@media (max-width: 640px)': { display: 'none' } }}>
              <ArrivalAvatar sx={{ background: '#be9dfd', color: '#2d1a54' }}>T</ArrivalAvatar>
              Theo left a voice note
            </Arrival>
            <Phone>
              <PhoneScreen>
                <PhoneImg src={photo('a01-wedding')} alt='' />
              </PhoneScreen>
              <PhoneBar>
                <Box sx={{ color: '#fff', fontSize: 13, fontWeight: 680, letterSpacing: '-0.02em' }}>
                  Elena &amp; Marcus
                </Box>
                <Box
                  sx={{
                    fontFamily: 'var(--ds-font-mono)',
                    fontSize: 7.5,
                    letterSpacing: '0.12em',
                    color: 'var(--ds-accent-deep-b)',
                  }}
                >
                  ● 12 INSIDE
                </Box>
              </PhoneBar>
            </Phone>
            <ScanBeam />
            <KeyCardWrap>
              <KeyCard name='Elena & Marcus' url='EGOWALL.COM/A/EM-0614' />
            </KeyCardWrap>
          </Art>
        </Reveal>
      </Wrap>

      <Base>
        <Marquee>
          {marqueeAlbums.map((a) => (
            <Box
              key={a.name}
              sx={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: 14, fontWeight: 620, letterSpacing: '-0.015em', color: 'var(--ds-ink-muted)', whiteSpace: 'nowrap' }}
            >
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: a.dot }} />
              {a.name}{' '}
              <Box component='span' sx={{ fontFamily: 'var(--ds-font-mono)', fontSize: 8.5, letterSpacing: '0.1em', color: 'var(--ds-ink-subtle)' }}>
                {a.meta}
              </Box>
            </Box>
          ))}
        </Marquee>
      </Base>
    </Header>
  )
}
